import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getDocuments } from '../../api/endpoints/admin';
import { getMyScorecard, getMyWork } from '../../api/endpoints/ops';
import { operationalQueryPolicy, safePagePlaceholder } from '../../api/queryPolicies';
import { shipmentStatusColor } from '../../components/domain/statusColor';
import { DataTable } from '../../components/ui/DataTable';
import { Pagination } from '../../components/ui/Pagination';
import { RefreshButton } from '../../components/ui/RefreshButton';
import { shipmentStatusLabel } from '../shipments/shipmentStatusLabel';
import { StatusPill } from '../../components/ui/StatusPill';
import { ScorecardStrip } from '../scorecard/ScorecardStrip';
import { BiddingClosedPanel } from './BiddingClosedPanel';
import { useOpsUser } from '../ownership/opsUser';
import { fmtAge, fmtDate } from './workFormat';
import { VehicleCell } from '../../components/domain/VehicleCell';
import styles from './WorkQueue.module.css';
import { usePaymentQueue } from '../payments/hooks';
import { RouteText } from '../../components/domain/RouteText';

const PAGE_SIZE = 25;
const DOCS_TO_REVIEW_KEY = ['admin', 'documents', 'to-review-count'];

/** Default landing page: the caller's open orders (open stop first, then oldest owned; dnm-work#165 C5). */
export function MyWorkView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const { opsUser } = useOpsUser();
  const [activeChipKey, setActiveChipKey] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<{
    key: string;
    label: string;
    ids: number[];
  } | null>(null);

  const queryKey = ['admin', 'work', 'mine', page] as const;
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: () => getMyWork({ page, size: PAGE_SIZE }),
    ...operationalQueryPolicy,
    placeholderData: safePagePlaceholder(queryKey, 3),
  });

  const scorecardQuery = useQuery({
    queryKey: ['admin', 'scorecard', 'me'],
    queryFn: () => getMyScorecard(),
    ...operationalQueryPolicy,
  });
  const docsToReviewQuery = useQuery({
    queryKey: DOCS_TO_REVIEW_KEY,
    queryFn: () => getDocuments({ status: 'UPLOADED', page: 0, size: 1 }),
    ...operationalQueryPolicy,
  });
  // Unknown (loading or failed) stays undefined so the chip shows "—", never a fake 0.
  const docsToReviewCount = docsToReviewQuery.isError
    ? undefined
    : docsToReviewQuery.data?.totalItems;
  const paymentQueue = usePaymentQueue(true);
  const paymentBreached =
    (paymentQueue.data?.topStrip.breachedOrders ?? 0) +
    (paymentQueue.data?.topStrip.breachedPayouts ?? 0) +
    (paymentQueue.data?.topStrip.breachedClaims ?? 0);
  const paymentAtRisk =
    (paymentQueue.data?.topStrip.atRiskOrders ?? 0) +
    (paymentQueue.data?.topStrip.atRiskPayouts ?? 0) +
    (paymentQueue.data?.topStrip.atRiskClaims ?? 0);

  const handleChipClick = (key: string, shipmentIds: number[], isUnassigned: boolean) => {
    if (isUnassigned) {
      navigate('/unassigned', {
        state: { filterIds: shipmentIds, filterLabel: 'Unclaimed orders' },
      });
      return;
    }

    if (activeChipKey === key) {
      setActiveChipKey(null);
      setActiveFilter(null);
    } else if (key === 'biddingClosed') {
      // Team-wide, usually not in my own work page: shown as its own list, not a table filter.
      setActiveChipKey(key);
      setActiveFilter(null);
    } else {
      const labels: Record<string, string> = {
        driver: 'Awaiting driver',
        pickup: 'Pickup at risk',
        pod: 'POD not closed',
        stopped: 'Stopped',
        participantKyc: 'KYC not verified',
      };
      setActiveChipKey(key);
      setActiveFilter({ key, label: labels[key] ?? key, ids: shipmentIds });
    }
  };

  const navState = { from: '/work', fromLabel: 'My Work' };
  const atCap = !!opsUser && opsUser.openOrderCount >= opsUser.effectiveMaxOpenOrders;

  const displayedRows = activeFilter
    ? (data?.items ?? []).filter((s) => activeFilter.ids.includes(s.id))
    : (data?.items ?? []);

  return (
    <div className={styles.page}>
      <ScorecardStrip
        scorecard={scorecardQuery.data}
        isLoading={scorecardQuery.isLoading}
        activeChipKey={activeChipKey}
        onChipClick={handleChipClick}
        docsToReviewCount={docsToReviewCount}
      />
      {(paymentBreached > 0 || paymentAtRisk > 0) && (
        <div className={styles.toolbar}>
          {t('work.myWorkView.paymentsPrefix')}{' '}
          <Link className={styles.paymentSummaryLink} to="/payments?state=BREACHED&mine=true">
            {t('work.myWorkView.breached', { n: paymentBreached })}
          </Link>{' '}
          ·{' '}
          <Link className={styles.paymentSummaryLink} to="/payments?state=AT_RISK&mine=true">
            {t('work.myWorkView.atRisk', { n: paymentAtRisk })}
          </Link>
        </div>
      )}

      <div className={styles.toolbar}>
        <div className={styles.summary}>
          {opsUser ? (
            <span className={atCap ? styles.atCap : undefined}>
              <strong>{opsUser.openOrderCount}</strong>{' '}
              {t('work.myWorkView.ofCapOpenOrders', { cap: opsUser.effectiveMaxOpenOrders })}
              {atCap ? ' — at cap, release or close an order before claiming more' : ''}
            </span>
          ) : (
            'Your open orders'
          )}
        </div>
        <RefreshButton
          queryKeys={[
            ['admin', 'work'],
            ['admin', 'ops-users', 'me'],
            ['admin', 'scorecard', 'me'],
            DOCS_TO_REVIEW_KEY,
          ]}
        />
      </div>

      {activeChipKey === 'biddingClosed' && scorecardQuery.data && (
        <BiddingClosedPanel
          shipmentIds={scorecardQuery.data.needsAttention.biddingClosed.shipmentIds}
        />
      )}

      {activeFilter && (
        <div className={styles.filterBanner}>
          <span>
            {t('work.myWorkView.filteredBy')} <strong>{activeFilter.label}</strong>{' '}
            {t('work.myWorkView.filteredCount', { count: displayedRows.length })}
          </span>
          <button
            type="button"
            className={styles.clearFilterBtn}
            onClick={() => {
              setActiveChipKey(null);
              setActiveFilter(null);
            }}
          >
            {t('work.myWorkView.clearFilter')}
          </button>
        </div>
      )}

      <DataTable
        loading={isLoading}
        error={isError ? 'We couldn’t load your work' : undefined}
        onRetry={() => refetch()}
        keyFn={(s) => s.id}
        rows={displayedRows}
        onRowClick={(s) => navigate(`/shipments/${s.id}`, { state: navState })}
        empty={
          activeFilter
            ? `No orders matching “${activeFilter.label}” on this page`
            : 'Nothing on your plate — claim orders from Unassigned'
        }
        columns={[
          {
            key: 'id',
            header: 'ID',
            width: '72px',
            render: (s) => (
              <Link to={`/shipments/${s.id}`} state={navState} className={styles.detailLink}>
                #{s.id}
              </Link>
            ),
          },
          {
            key: 'route',
            header: 'Route',
            render: (s) => (
              <Link to={`/shipments/${s.id}`} state={navState} className={styles.route}>
                <RouteText route={s} />
                <span className={styles.sub}>{s.shipperName ?? `Shipper #${s.shipperId}`}</span>
              </Link>
            ),
          },
          {
            key: 'vehicle',
            header: 'Vehicle',
            width: '140px',
            render: (s) => <VehicleCell assignment={s.assignment} />,
          },
          { key: 'pickup', header: 'Pickup', width: '120px', render: (s) => fmtDate(s.pickupDate) },
          {
            key: 'status',
            header: 'Status',
            width: '150px',
            render: (s) => {
              const c = shipmentStatusColor(s.status);
              return <StatusPill bg={c.bg} text={c.text} label={shipmentStatusLabel(s.status)} title={s.status} />;
            },
          },
          {
            key: 'ownedSince',
            header: 'Owned',
            width: '140px',
            render: (s) => (
              <span
                title={s.ownedSince ? new Date(s.ownedSince).toLocaleString('en-IN') : undefined}
              >
                {fmtAge(s.ownedSince)}
                <span className={styles.sub}>{t('work.myWorkView.since', { date: fmtDate(s.ownedSince) })}</span>
              </span>
            ),
          },
        ]}
      />

      {data && (
        <Pagination
          page={data.page}
          totalPages={data.totalPages}
          totalItems={data.totalItems}
          size={data.size}
          onPage={setPage}
        />
      )}
    </div>
  );
}
